import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

interface Category {
  id: string;
  icon: string;
  name_it: string;
  name_en: string | null;
  name_es: string | null;
  keywords_it: string[] | null;
  keywords_en: string[] | null;
  keywords_es: string[] | null;
  display_order: number;
  is_active: boolean;
}

interface CategoryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
  onSuccess: () => void;
}

export const CategoryFormModal = ({ open, onOpenChange, category, onSuccess }: CategoryFormModalProps) => {
  const { t } = useTranslation();
  const isEditing = !!category;

  const [formData, setFormData] = useState({
    id: "",
    icon: "",
    name_it: "",
    name_en: "",
    name_es: "",
    keywords_it: "",
    keywords_en: "",
    keywords_es: "",
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (category) {
      setFormData({
        id: category.id,
        icon: category.icon,
        name_it: category.name_it,
        name_en: category.name_en || "",
        name_es: category.name_es || "",
        keywords_it: (category.keywords_it || []).join(", "),
        keywords_en: (category.keywords_en || []).join(", "),
        keywords_es: (category.keywords_es || []).join(", "),
        display_order: category.display_order,
        is_active: category.is_active,
      });
    } else {
      setFormData({
        id: "",
        icon: "",
        name_it: "",
        name_en: "",
        name_es: "",
        keywords_it: "",
        keywords_en: "",
        keywords_es: "",
        display_order: 0,
        is_active: true,
      });
    }
  }, [category, open]);

  const parseKeywords = (str: string): string[] =>
    str.split(",").map((s) => s.trim()).filter(Boolean);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        id: formData.id.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
        icon: formData.icon,
        name_it: formData.name_it,
        name_en: formData.name_en || null,
        name_es: formData.name_es || null,
        keywords_it: parseKeywords(formData.keywords_it),
        keywords_en: parseKeywords(formData.keywords_en),
        keywords_es: parseKeywords(formData.keywords_es),
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("professional_categories")
          .update(payload)
          .eq("id", category.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("professional_categories")
          .insert(payload);
        if (error) {
          if (error.code === "23505") {
            throw new Error("duplicate");
          }
          throw error;
        }
      }
    },
    onSuccess: () => {
      toast({ title: isEditing ? t("admin.categories.updateSuccess") : t("admin.categories.createSuccess") });
      onSuccess();
    },
    onError: (err: Error) => {
      if (err.message === "duplicate") {
        toast({ title: t("admin.categories.duplicateId"), variant: "error" });
      } else {
        toast({ title: isEditing ? t("admin.categories.updateError") : t("admin.categories.createError"), variant: "error" });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.id || !formData.name_it || !formData.icon) return;
    saveMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("admin.categories.editCategory") : t("admin.categories.addCategory")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("admin.categories.form.id")} *</Label>
              <BrutalInput
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                placeholder={t("admin.categories.form.idPlaceholder")}
                disabled={isEditing}
                required
              />
              <p className="text-xs text-muted-foreground">{t("admin.categories.form.idHelper")}</p>
            </div>
            <div className="space-y-2">
              <Label>{t("admin.categories.form.icon")} *</Label>
              <BrutalInput
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder={t("admin.categories.form.iconPlaceholder")}
                className="text-2xl"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t("admin.categories.form.nameIt")} *</Label>
              <BrutalInput
                value={formData.name_it}
                onChange={(e) => setFormData({ ...formData, name_it: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.categories.form.nameEn")}</Label>
              <BrutalInput
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.categories.form.nameEs")}</Label>
              <BrutalInput
                value={formData.name_es}
                onChange={(e) => setFormData({ ...formData, name_es: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t("admin.categories.form.keywordsIt")}</Label>
              <BrutalTextarea
                value={formData.keywords_it}
                onChange={(e) => setFormData({ ...formData, keywords_it: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.categories.form.keywordsEn")}</Label>
              <BrutalTextarea
                value={formData.keywords_en}
                onChange={(e) => setFormData({ ...formData, keywords_en: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.categories.form.keywordsEs")}</Label>
              <BrutalTextarea
                value={formData.keywords_es}
                onChange={(e) => setFormData({ ...formData, keywords_es: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("admin.categories.form.keywordsHelper")}</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("admin.categories.form.displayOrder")}</Label>
              <BrutalInput
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>{t("admin.categories.form.isActive")}</Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <BrutalButton type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t("common.saving") : t("common.save")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
